import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Refund {
  id: string;
  refundNumber: string;
  originalOrder: string;
  customer: string;
  items: number;
  amount: number;
  reason: string;
  status: "pending" | "approved" | "processed" | "rejected";
  date: string;
  time: string;
  processedBy?: string;
  refundMethod: string;
}

const MOCK_REFUNDS: Refund[] = [
  { id: "1", refundNumber: "REF-2024-001", originalOrder: "ORD-2024-015", customer: "John Doe", items: 1, amount: 45.99, reason: "Defective product", status: "processed", date: "2024-01-15", time: "10:30 AM", processedBy: "Maria G.", refundMethod: "Original payment" },
  { id: "2", refundNumber: "REF-2024-002", originalOrder: "ORD-2024-018", customer: "Jane Smith", items: 2, amount: 89.50, reason: "Wrong item shipped", status: "approved", date: "2024-01-15", time: "11:15 AM", refundMethod: "Store credit" },
  { id: "3", refundNumber: "REF-2024-003", originalOrder: "ORD-2024-022", customer: "Mike Wilson", items: 1, amount: 25.00, reason: "Customer changed mind", status: "pending", date: "2024-01-15", time: "12:00 PM", refundMethod: "Original payment" },
  { id: "4", refundNumber: "REF-2024-004", originalOrder: "ORD-2024-025", customer: "Sarah Johnson", items: 3, amount: 156.75, reason: "Product not as described", status: "pending", date: "2024-01-15", time: "02:30 PM", refundMethod: "Original payment" },
  { id: "5", refundNumber: "REF-2024-005", originalOrder: "ORD-2024-030", customer: "Alex Brown", items: 1, amount: 35.00, reason: "Duplicate order", status: "processed", date: "2024-01-14", time: "04:45 PM", processedBy: "Carlos R.", refundMethod: "Original payment" },
  { id: "6", refundNumber: "REF-2024-006", originalOrder: "ORD-2024-032", customer: "Emily Davis", items: 2, amount: 67.00, reason: "Fraudulent claim", status: "rejected", date: "2024-01-14", time: "05:20 PM", refundMethod: "N/A" },
];

export default function RefundsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredRefunds = MOCK_REFUNDS.filter((refund) => {
    const matchesSearch = refund.refundNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.originalOrder.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || refund.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: Refund["status"]) => {
    const styles = {
      pending: "bg-amber-500/20 text-amber-500",
      approved: "bg-blue-500/20 text-blue-500",
      processed: "bg-emerald-500/20 text-emerald-500",
      rejected: "bg-red-500/20 text-red-500",
    };
    const labels = {
      pending: t("refunds.statusPending"),
      approved: t("refunds.statusApproved"),
      processed: t("refunds.statusProcessed"),
      rejected: t("refunds.statusRejected"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const pendingRefunds = MOCK_REFUNDS.filter((r) => r.status === "pending").length;
  const approvedRefunds = MOCK_REFUNDS.filter((r) => r.status === "approved").length;
  const totalRefunded = MOCK_REFUNDS.filter((r) => r.status === "processed")
    .reduce((acc, r) => acc + r.amount, 0);
  const monthlyRefunds = MOCK_REFUNDS.length;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.refunds")}</h1>
          <p className={themeClasses.textSecondary}>{t("refunds.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("refunds.newRefund")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("refunds.pendingRefunds")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingRefunds}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("refunds.approvedRefunds")}</span>
            <i className="ri-checkbox-circle-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-blue-500`}>{approvedRefunds}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("refunds.totalRefunded")}</span>
            <i className="ri-refund-2-line text-red-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-red-500`}>${totalRefunded.toFixed(2)}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("refunds.monthlyRefunds")}</span>
            <i className="ri-calendar-line text-gray-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{monthlyRefunds}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("refunds.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allStatuses")}</option>
              <option value="pending">{t("refunds.statusPending")}</option>
              <option value="approved">{t("refunds.statusApproved")}</option>
              <option value="processed">{t("refunds.statusProcessed")}</option>
              <option value="rejected">{t("refunds.statusRejected")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("refunds.refundNumber")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("refunds.originalOrder")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("refunds.reason")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("refunds.amount")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("refunds.method")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRefunds.map((refund) => (
                <tr key={refund.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{refund.refundNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{refund.date} {refund.time}</p>
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>
                    <span className="text-blue-500 hover:underline cursor-pointer">{refund.originalOrder}</span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>{refund.customer}</td>
                  <td className={`px-4 py-3 ${themeClasses.textSecondary} max-w-[200px] truncate`}>{refund.reason}</td>
                  <td className={`px-4 py-3 text-right font-bold text-red-500`}>${refund.amount.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-center ${themeClasses.textSecondary}`}>{refund.refundMethod}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(refund.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      {refund.status === "pending" && (
                        <>
                          <button className="p-2 rounded-lg hover:bg-emerald-500/10 text-emerald-500">
                            <i className="ri-check-line"></i>
                          </button>
                          <button aria-label={t("general.close", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                            <i className="ri-close-line"></i>
                          </button>
                        </>
                      )}
                      {refund.status === "approved" && (
                        <button className="p-2 rounded-lg hover:bg-blue-500/10 text-blue-500">
                          <i className="ri-money-dollar-circle-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
